/**
 * `/poste` — the reception desk, a kiosk route tree deliberately outside
 * `_dashboard`.
 *
 * `_dashboard` is the member console: a sidebar, an org switcher, a user menu,
 * breadcrumbs. None of that belongs on a shared machine at a lobby desk, where
 * the operator is a gardien with one job and the screen is read at arm's
 * length. So the station gets its own shell, its own dark surface, and its own
 * gate.
 *
 * That gate runs here, on the layout, so every child inherits it — see
 * `~/lib/station/route` for the decision itself and why it has to tolerate
 * desk-to-desk navigation.
 */
import type { QueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";

import type { StationState } from "~/lib/station/route";
import { orpc } from "~/lib/orpc";
import { getDeviceToken, getMustChangePin } from "~/lib/station/device-token";
import { resolveStationStage, stationRedirect } from "~/lib/station/route";

/**
 * oRPC error codes that mean "this browser holds no usable gardien session".
 * Anything else — a network failure, a 500 — is a real fault and must surface
 * as an error rather than silently dumping the desk back at the PIN pad, which
 * would read to the gardien as "my PIN stopped working".
 */
const NO_SESSION_CODES = ["UNAUTHORIZED", "NOT_AUTHENTICATED", "FORBIDDEN"];

async function readStationState(
  queryClient: QueryClient,
): Promise<StationState> {
  const hasDeviceToken = getDeviceToken() !== null;
  if (!hasDeviceToken) {
    return {
      hasDeviceToken: false,
      hasSession: false,
      mustChangePin: false,
      hasOpenShift: false,
    };
  }
  try {
    // `shift.current` answers both remaining questions in one round trip: it
    // is a `memberProcedure`, so reaching it at all proves the session, and
    // its result is the open shift.
    const shift = await queryClient.ensureQueryData(
      orpc.shift.current.queryOptions(),
    );
    return {
      hasDeviceToken: true,
      hasSession: true,
      mustChangePin: getMustChangePin(),
      hasOpenShift: shift !== null,
    };
  } catch (err) {
    const code = (err as { code?: string }).code;
    if (code !== undefined && NO_SESSION_CODES.includes(code)) {
      return {
        hasDeviceToken: true,
        hasSession: false,
        mustChangePin: false,
        hasOpenShift: false,
      };
    }
    throw err;
  }
}

export const Route = createFileRoute("/poste")({
  beforeLoad: async ({ context, location }) => {
    const state = await readStationState(context.queryClient);
    const target = stationRedirect(
      resolveStationStage(state),
      location.pathname,
    );
    if (target) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: target, replace: true });
    }
  },
  component: StationLayout,
});

function StationLayout() {
  return (
    <div className="station bg-background text-foreground min-h-dvh">
      <Outlet />
    </div>
  );
}
